import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UserSessionService } from '../../services/user-session.service';
import {
  MystoreService,
  MyStoreBudget,
  MyStoreCashSession,
  MyStoreCategory,
  MyStoreClient,
  MyStoreItem,
  MyStorePurchase,
  MyStoreSale,
  MyStoreStore,
  MyStoreStoreUser,
  MyStoreSupplier
} from '../../services/mystore.service';

type MyStoreTab = 'dashboard' | 'stores' | 'clients' | 'catalog' | 'stock' | 'budgets' | 'sales' | 'purchases' | 'cash' | 'reports';

@Component({
  selector: 'app-mystore',
  templateUrl: './mystore.component.html',
  styleUrls: ['./mystore.component.scss']
})
export class MystoreComponent implements OnInit {
  tenantId: number | null = null;
  userId: number | null = null;
  currentUser: any = null;
  activeTab: MyStoreTab = 'dashboard';
  loading = false;
  feedback = '';
  errorMessage = '';

  dashboardSummary: any = {};
  dashboardMonthlySales: any[] = [];

  lookups = {
    stores: [] as MyStoreStore[],
    users: [] as any[],
    clients: [] as MyStoreClient[],
    categories: [] as MyStoreCategory[],
    items: [] as MyStoreItem[],
    suppliers: [] as MyStoreSupplier[]
  };

  stores: MyStoreStore[] = [];
  clients: MyStoreClient[] = [];
  categories: MyStoreCategory[] = [];
  items: MyStoreItem[] = [];
  suppliers: MyStoreSupplier[] = [];
  stockPositions: any[] = [];
  stockMovements: any[] = [];
  budgets: MyStoreBudget[] = [];
  sales: MyStoreSale[] = [];
  purchases: MyStorePurchase[] = [];
  cashSessions: MyStoreCashSession[] = [];
  cashMovements: any[] = [];
  storeUsers: MyStoreStoreUser[] = [];
  revenueReport: any[] = [];
  stockReport: any[] = [];
  salesReport: any[] = [];

  storesTotal = 0;
  clientsTotal = 0;
  categoriesTotal = 0;
  itemsTotal = 0;
  suppliersTotal = 0;
  budgetsTotal = 0;
  salesTotal = 0;
  purchasesTotal = 0;

  filters = {
    stores: { q: '', page: 1, page_size: 8 },
    clients: { q: '', page: 1, page_size: 8 },
    categories: { q: '', page: 1, page_size: 8 },
    items: { q: '', page: 1, page_size: 8, item_type: '' },
    suppliers: { q: '', page: 1, page_size: 8 },
    budgets: { page: 1, page_size: 8 },
    sales: { page: 1, page_size: 8 },
    purchases: { page: 1, page_size: 8 },
    stock: { store_id: '' }
  };

  selectedStore: MyStoreStore | null = null;
  selectedClient: MyStoreClient | null = null;
  selectedCategory: MyStoreCategory | null = null;
  selectedItem: MyStoreItem | null = null;
  selectedSupplier: MyStoreSupplier | null = null;
  selectedBudget: MyStoreBudget | null = null;
  selectedSale: MyStoreSale | null = null;
  selectedPurchase: MyStorePurchase | null = null;
  selectedCashSession: MyStoreCashSession | null = null;

  showStoreModal = false;
  showStoreUsersModal = false;
  showClientModal = false;
  showCategoryModal = false;
  showItemModal = false;
  showSupplierModal = false;
  showBudgetModal = false;
  showSaleModal = false;
  showPurchaseModal = false;
  showStockMovementModal = false;
  showCashSessionModal = false;
  showCashMovementModal = false;
  showPaymentModal = false;

  storeForm: FormGroup;
  storeUserForm: FormGroup;
  clientForm: FormGroup;
  categoryForm: FormGroup;
  itemForm: FormGroup;
  supplierForm: FormGroup;
  budgetForm: FormGroup;
  saleForm: FormGroup;
  purchaseForm: FormGroup;
  stockMovementForm: FormGroup;
  cashSessionForm: FormGroup;
  cashMovementForm: FormGroup;
  paymentForm: FormGroup;

  readonly tabs: Array<{ key: MyStoreTab; label: string; icon: string }> = [
    { key: 'dashboard', label: 'Dashboard', icon: 'dashboard' },
    { key: 'stores', label: 'Lojas', icon: 'storefront' },
    { key: 'clients', label: 'Clientes', icon: 'groups' },
    { key: 'catalog', label: 'Catálogo', icon: 'inventory_2' },
    { key: 'stock', label: 'Estoque', icon: 'warehouse' },
    { key: 'budgets', label: 'Orçamentos', icon: 'request_quote' },
    { key: 'sales', label: 'Vendas', icon: 'point_of_sale' },
    { key: 'purchases', label: 'Compras', icon: 'local_shipping' },
    { key: 'cash', label: 'Caixa', icon: 'account_balance_wallet' },
    { key: 'reports', label: 'Relatórios', icon: 'bar_chart' }
  ];

  constructor(
    private fb: FormBuilder,
    private userSession: UserSessionService,
    private mystoreService: MystoreService
  ) {
    this.storeForm = this.fb.group({
      code: [''],
      name: ['', Validators.required],
      store_type: ['MATRIX', Validators.required],
      matrix_store_id: [''],
      manager_user_id: [''],
      address_zip: [''],
      address_street: [''],
      address_number: [''],
      address_complement: [''],
      address_neighborhood: [''],
      address_city: [''],
      address_state: [''],
      address_country: ['Brasil'],
      active: [true]
    });

    this.storeUserForm = this.fb.group({
      user_id: ['', Validators.required],
      role_name: ['', Validators.required],
      can_manage: [false],
      can_sell: [true],
      can_buy: [false],
      can_stock: [false],
      can_cash: [false],
      active: [true]
    });

    this.clientForm = this.fb.group({
      code: [''],
      name: ['', Validators.required],
      document_number: [''],
      email: [''],
      phone: [''],
      mobile_phone: [''],
      address_city: [''],
      address_state: [''],
      notes: [''],
      active: [true]
    });

    this.categoryForm = this.fb.group({
      name: ['', Validators.required],
      description: [''],
      active: [true]
    });

    this.itemForm = this.fb.group({
      category_id: [''],
      code: [''],
      name: ['', Validators.required],
      item_type: ['PRODUCT', Validators.required],
      sku: [''],
      unit_name: ['UN'],
      cost_price: [0, Validators.required],
      sale_price: [0, Validators.required],
      stock_enabled: [true],
      minimum_stock: [0],
      active: [true]
    });

    this.supplierForm = this.fb.group({
      name: ['', Validators.required],
      document_number: [''],
      email: [''],
      phone: [''],
      contact_name: [''],
      notes: [''],
      active: [true]
    });

    this.budgetForm = this.fb.group({
      store_id: ['', Validators.required],
      client_id: [''],
      seller_user_id: [''],
      status: ['DRAFT', Validators.required],
      valid_until: [''],
      notes: [''],
      items: this.fb.array([])
    });

    this.saleForm = this.fb.group({
      store_id: ['', Validators.required],
      client_id: [''],
      seller_user_id: [''],
      source_budget_id: [''],
      status: ['OPEN', Validators.required],
      sold_at: [''],
      notes: [''],
      items: this.fb.array([]),
      payments: this.fb.array([])
    });

    this.purchaseForm = this.fb.group({
      store_id: ['', Validators.required],
      supplier_id: [''],
      buyer_user_id: [''],
      status: ['OPEN', Validators.required],
      purchased_at: [''],
      notes: [''],
      items: this.fb.array([])
    });

    this.stockMovementForm = this.fb.group({
      store_id: ['', Validators.required],
      item_id: ['', Validators.required],
      movement_type: ['ENTRY', Validators.required],
      quantity: [1, Validators.required],
      unit_cost: [0],
      notes: ['']
    });

    this.cashSessionForm = this.fb.group({
      store_id: ['', Validators.required],
      opening_amount: [0, Validators.required],
      notes: ['']
    });

    this.cashMovementForm = this.fb.group({
      movement_type: ['IN', Validators.required],
      category: ['', Validators.required],
      amount: [0, Validators.required],
      moved_at: [''],
      notes: ['']
    });

    this.paymentForm = this.fb.group({
      payment_method: ['PIX', Validators.required],
      amount: [0, Validators.required],
      paid_at: [''],
      notes: ['']
    });
  }

  ngOnInit(): void {
    const user = this.userSession.getUser();
    this.currentUser = user;
    this.tenantId = Number(user?.tenant_id || 0) || null;
    this.userId = Number(user?.id || 0) || null;
    if (!this.tenantId || !this.userId) {
      this.errorMessage = 'Sessão inválida para carregar o módulo.';
      return;
    }
    this.loadAll();
  }

  get budgetItems(): FormArray {
    return this.budgetForm.get('items') as FormArray;
  }

  get saleItems(): FormArray {
    return this.saleForm.get('items') as FormArray;
  }

  get salePayments(): FormArray {
    return this.saleForm.get('payments') as FormArray;
  }

  get purchaseItems(): FormArray {
    return this.purchaseForm.get('items') as FormArray;
  }

  private createLineItemGroup(item?: any, mode: 'sale' | 'purchase' = 'sale'): FormGroup {
    return this.fb.group({
      item_id: [item?.item_id || ''],
      description: [item?.description || '', Validators.required],
      quantity: [item?.quantity || 1, Validators.required],
      unit_price: [mode === 'sale' ? (item?.unit_price ?? 0) : null],
      unit_cost: [mode === 'purchase' ? (item?.unit_cost ?? 0) : null],
      discount_amount: [item?.discount_amount ?? 0]
    });
  }

  private createPaymentGroup(payment?: any): FormGroup {
    return this.fb.group({
      payment_method: [payment?.payment_method || 'PIX', Validators.required],
      amount: [payment?.amount || 0, Validators.required],
      paid_at: [payment?.paid_at || ''],
      notes: [payment?.notes || '']
    });
  }

  addBudgetItem(item?: any): void {
    this.budgetItems.push(this.createLineItemGroup(item, 'sale'));
  }

  removeBudgetItem(index: number): void {
    this.budgetItems.removeAt(index);
  }

  addSaleItem(item?: any): void {
    this.saleItems.push(this.createLineItemGroup(item, 'sale'));
  }

  removeSaleItem(index: number): void {
    this.saleItems.removeAt(index);
  }

  addSalePaymentLine(payment?: any): void {
    this.salePayments.push(this.createPaymentGroup(payment));
  }

  removeSalePaymentLine(index: number): void {
    this.salePayments.removeAt(index);
  }

  addPurchaseItem(item?: any): void {
    this.purchaseItems.push(this.createLineItemGroup(item, 'purchase'));
  }

  removePurchaseItem(index: number): void {
    this.purchaseItems.removeAt(index);
  }

  switchTab(tab: MyStoreTab): void {
    this.activeTab = tab;
  }

  private loadAll(): void {
    this.loadLookups();
    this.loadDashboard();
    this.loadStores();
    this.loadClients();
    this.loadCategories();
    this.loadItems();
    this.loadSuppliers();
    this.loadStock();
    this.loadBudgets();
    this.loadSales();
    this.loadPurchases();
    this.loadCashSessions();
    this.loadReports();
  }

  private loadLookups(): void {
    if (!this.tenantId) return;
    this.mystoreService.getLookups(this.tenantId).subscribe({
      next: (response) => {
        this.lookups = {
          stores: response?.stores || [],
          users: response?.users || [],
          clients: response?.clients || [],
          categories: response?.categories || [],
          items: response?.items || [],
          suppliers: response?.suppliers || []
        };
      },
      error: () => {
        this.errorMessage = 'Não foi possível carregar os dados auxiliares do My Store.';
      }
    });
  }

  private loadDashboard(): void {
    if (!this.tenantId) return;
    this.mystoreService.getDashboard(this.tenantId).subscribe({
      next: (response) => {
        this.dashboardSummary = response?.summary || {};
        this.dashboardMonthlySales = response?.monthly_sales || [];
      },
      error: () => {
        this.errorMessage = 'Não foi possível carregar o dashboard do My Store.';
      }
    });
  }

  loadStores(): void {
    if (!this.tenantId) return;
    this.mystoreService.getStores(this.tenantId, this.filters.stores).subscribe({
      next: (response) => {
        this.stores = response.rows || [];
        this.storesTotal = Number(response.total || 0);
      }
    });
  }

  loadClients(): void {
    if (!this.tenantId) return;
    this.mystoreService.getClients(this.tenantId, this.filters.clients).subscribe({
      next: (response) => {
        this.clients = response.rows || [];
        this.clientsTotal = Number(response.total || 0);
      }
    });
  }

  loadCategories(): void {
    if (!this.tenantId) return;
    this.mystoreService.getCategories(this.tenantId, this.filters.categories).subscribe({
      next: (response) => {
        this.categories = response.rows || [];
        this.categoriesTotal = Number(response.total || 0);
      }
    });
  }

  loadItems(): void {
    if (!this.tenantId) return;
    this.mystoreService.getItems(this.tenantId, this.filters.items).subscribe({
      next: (response) => {
        this.items = response.rows || [];
        this.itemsTotal = Number(response.total || 0);
      }
    });
  }

  loadSuppliers(): void {
    if (!this.tenantId) return;
    this.mystoreService.getSuppliers(this.tenantId, this.filters.suppliers).subscribe({
      next: (response) => {
        this.suppliers = response.rows || [];
        this.suppliersTotal = Number(response.total || 0);
      }
    });
  }

  loadStock(): void {
    if (!this.tenantId) return;
    this.mystoreService.getStockPositions(this.tenantId, this.filters.stock).subscribe({
      next: (rows) => {
        this.stockPositions = rows || [];
      }
    });
    this.mystoreService.getStockMovements(this.tenantId, this.filters.stock).subscribe({
      next: (rows) => {
        this.stockMovements = rows || [];
      }
    });
  }

  loadBudgets(): void {
    if (!this.tenantId) return;
    this.mystoreService.getBudgets(this.tenantId, this.filters.budgets).subscribe({
      next: (response) => {
        this.budgets = response.rows || [];
        this.budgetsTotal = Number(response.total || 0);
      }
    });
  }

  loadSales(): void {
    if (!this.tenantId) return;
    this.mystoreService.getSales(this.tenantId, this.filters.sales).subscribe({
      next: (response) => {
        this.sales = response.rows || [];
        this.salesTotal = Number(response.total || 0);
      }
    });
  }

  loadPurchases(): void {
    if (!this.tenantId) return;
    this.mystoreService.getPurchases(this.tenantId, this.filters.purchases).subscribe({
      next: (response) => {
        this.purchases = response.rows || [];
        this.purchasesTotal = Number(response.total || 0);
      }
    });
  }

  loadCashSessions(): void {
    if (!this.tenantId) return;
    this.mystoreService.getCashSessions(this.tenantId).subscribe({
      next: (rows) => {
        this.cashSessions = rows || [];
      }
    });
  }

  loadReports(): void {
    if (!this.tenantId) return;
    this.mystoreService.getRevenueReport(this.tenantId).subscribe({ next: (rows) => this.revenueReport = rows || [] });
    this.mystoreService.getStockReport(this.tenantId).subscribe({ next: (rows) => this.stockReport = rows || [] });
    this.mystoreService.getSalesReport(this.tenantId).subscribe({ next: (rows) => this.salesReport = rows || [] });
  }

  private resetFeedback(): void {
    this.feedback = '';
    this.errorMessage = '';
  }

  private withAudit(payload: any): any {
    return {
      ...payload,
      tenant_id: this.tenantId,
      user_id: this.userId,
      created_by: this.userId,
      updated_by: this.userId
    };
  }

  openStoreModal(store?: MyStoreStore): void {
    this.resetFeedback();
    this.selectedStore = store || null;
    this.storeForm.reset({
      code: store?.code || '',
      name: store?.name || '',
      store_type: store?.store_type || 'MATRIX',
      matrix_store_id: store?.matrix_store_id || '',
      manager_user_id: store?.manager_user_id || '',
      address_zip: store?.address_zip || '',
      address_street: store?.address_street || '',
      address_number: store?.address_number || '',
      address_complement: store?.address_complement || '',
      address_neighborhood: store?.address_neighborhood || '',
      address_city: store?.address_city || '',
      address_state: store?.address_state || '',
      address_country: store?.address_country || 'Brasil',
      active: store ? Number(store.active) === 1 : true
    });
    this.showStoreModal = true;
  }

  submitStore(): void {
    if (this.storeForm.invalid) return;
    const payload = this.withAudit(this.storeForm.getRawValue());
    const request = this.selectedStore
      ? this.mystoreService.updateStore(this.selectedStore.id, payload)
      : this.mystoreService.createStore(payload);
    request.subscribe({
      next: () => {
        this.feedback = this.selectedStore ? 'Loja atualizada com sucesso.' : 'Loja criada com sucesso.';
        this.showStoreModal = false;
        this.loadLookups();
        this.loadStores();
        this.loadDashboard();
      },
      error: (error) => this.errorMessage = error?.error?.error || 'Falha ao salvar a loja.'
    });
  }

  deleteStore(store: MyStoreStore): void {
    if (!this.tenantId || !this.userId) return;
    this.mystoreService.deleteStore(this.tenantId, this.userId, store.id).subscribe({
      next: () => {
        this.feedback = 'Loja removida com sucesso.';
        this.loadStores();
        this.loadLookups();
      },
      error: (error) => this.errorMessage = error?.error?.error || 'Falha ao excluir a loja.'
    });
  }

  openStoreUsersModal(store: MyStoreStore): void {
    if (!this.tenantId) return;
    this.selectedStore = store;
    this.storeUserForm.reset({
      user_id: '',
      role_name: '',
      can_manage: false,
      can_sell: true,
      can_buy: false,
      can_stock: false,
      can_cash: false,
      active: true
    });
    this.mystoreService.getStoreUsers(this.tenantId, store.id).subscribe({
      next: (rows) => {
        this.storeUsers = rows || [];
        this.showStoreUsersModal = true;
      },
      error: (error) => this.errorMessage = error?.error?.error || 'Falha ao carregar os vínculos da loja.'
    });
  }

  submitStoreUser(): void {
    if (!this.selectedStore || this.storeUserForm.invalid) return;
    const payload = this.withAudit(this.storeUserForm.getRawValue());
    this.mystoreService.addStoreUser(this.selectedStore.id, payload).subscribe({
      next: () => {
        this.feedback = 'Usuário vinculado à loja.';
        this.openStoreUsersModal(this.selectedStore as MyStoreStore);
      },
      error: (error) => this.errorMessage = error?.error?.error || 'Falha ao vincular o usuário.'
    });
  }

  deleteStoreUser(link: MyStoreStoreUser): void {
    if (!this.tenantId || !this.userId || !this.selectedStore) return;
    this.mystoreService.deleteStoreUser(this.tenantId, this.userId, link.id).subscribe({
      next: () => this.openStoreUsersModal(this.selectedStore as MyStoreStore),
      error: (error) => this.errorMessage = error?.error?.error || 'Falha ao remover o vínculo.'
    });
  }

  openClientModal(client?: MyStoreClient): void {
    this.selectedClient = client || null;
    this.clientForm.reset({
      code: client?.code || '',
      name: client?.name || '',
      document_number: client?.document_number || '',
      email: client?.email || '',
      phone: client?.phone || '',
      mobile_phone: client?.mobile_phone || '',
      address_city: client?.address_city || '',
      address_state: client?.address_state || '',
      notes: client?.notes || '',
      active: client ? Number(client.active) === 1 : true
    });
    this.showClientModal = true;
  }

  submitClient(): void {
    if (this.clientForm.invalid) return;
    const payload = this.withAudit(this.clientForm.getRawValue());
    const request = this.selectedClient
      ? this.mystoreService.updateClient(this.selectedClient.id, payload)
      : this.mystoreService.createClient(payload);
    request.subscribe({
      next: () => {
        this.showClientModal = false;
        this.feedback = 'Cliente salvo com sucesso.';
        this.loadClients();
        this.loadLookups();
      },
      error: (error) => this.errorMessage = error?.error?.error || 'Falha ao salvar o cliente.'
    });
  }

  deleteClient(client: MyStoreClient): void {
    if (!this.tenantId || !this.userId) return;
    this.mystoreService.deleteClient(this.tenantId, this.userId, client.id).subscribe({
      next: () => {
        this.feedback = 'Cliente removido com sucesso.';
        this.loadClients();
        this.loadLookups();
      },
      error: (error) => this.errorMessage = error?.error?.error || 'Falha ao excluir o cliente.'
    });
  }

  openCategoryModal(category?: MyStoreCategory): void {
    this.selectedCategory = category || null;
    this.categoryForm.reset({
      name: category?.name || '',
      description: category?.description || '',
      active: category ? Number(category.active) === 1 : true
    });
    this.showCategoryModal = true;
  }

  submitCategory(): void {
    if (this.categoryForm.invalid) return;
    const payload = this.withAudit(this.categoryForm.getRawValue());
    const request = this.selectedCategory
      ? this.mystoreService.updateCategory(this.selectedCategory.id, payload)
      : this.mystoreService.createCategory(payload);
    request.subscribe({
      next: () => {
        this.showCategoryModal = false;
        this.feedback = 'Categoria salva com sucesso.';
        this.loadCategories();
        this.loadLookups();
      },
      error: (error) => this.errorMessage = error?.error?.error || 'Falha ao salvar a categoria.'
    });
  }

  deleteCategory(category: MyStoreCategory): void {
    if (!this.tenantId || !this.userId) return;
    this.mystoreService.deleteCategory(this.tenantId, this.userId, category.id).subscribe({
      next: () => {
        this.feedback = 'Categoria removida com sucesso.';
        this.loadCategories();
        this.loadLookups();
      },
      error: (error) => this.errorMessage = error?.error?.error || 'Falha ao excluir a categoria.'
    });
  }

  openItemModal(item?: MyStoreItem): void {
    this.selectedItem = item || null;
    this.itemForm.reset({
      category_id: item?.category_id || '',
      code: item?.code || '',
      name: item?.name || '',
      item_type: item?.item_type || 'PRODUCT',
      sku: item?.sku || '',
      unit_name: item?.unit_name || 'UN',
      cost_price: item?.cost_price ?? 0,
      sale_price: item?.sale_price ?? 0,
      stock_enabled: item ? Number(item.stock_enabled) === 1 : true,
      minimum_stock: item?.minimum_stock ?? 0,
      active: item ? Number(item.active) === 1 : true
    });
    this.showItemModal = true;
  }

  submitItem(): void {
    if (this.itemForm.invalid) return;
    const payload = this.withAudit(this.itemForm.getRawValue());
    const request = this.selectedItem
      ? this.mystoreService.updateItem(this.selectedItem.id, payload)
      : this.mystoreService.createItem(payload);
    request.subscribe({
      next: () => {
        this.showItemModal = false;
        this.feedback = 'Item salvo com sucesso.';
        this.loadItems();
        this.loadLookups();
        this.loadStock();
      },
      error: (error) => this.errorMessage = error?.error?.error || 'Falha ao salvar o item.'
    });
  }

  deleteItem(item: MyStoreItem): void {
    if (!this.tenantId || !this.userId) return;
    this.mystoreService.deleteItem(this.tenantId, this.userId, item.id).subscribe({
      next: () => {
        this.feedback = 'Item removido com sucesso.';
        this.loadItems();
        this.loadLookups();
      },
      error: (error) => this.errorMessage = error?.error?.error || 'Falha ao excluir o item.'
    });
  }

  openSupplierModal(supplier?: MyStoreSupplier): void {
    this.selectedSupplier = supplier || null;
    this.supplierForm.reset({
      name: supplier?.name || '',
      document_number: supplier?.document_number || '',
      email: supplier?.email || '',
      phone: supplier?.phone || '',
      contact_name: supplier?.contact_name || '',
      notes: supplier?.notes || '',
      active: supplier ? Number(supplier.active) === 1 : true
    });
    this.showSupplierModal = true;
  }

  submitSupplier(): void {
    if (this.supplierForm.invalid) return;
    const payload = this.withAudit(this.supplierForm.getRawValue());
    const request = this.selectedSupplier
      ? this.mystoreService.updateSupplier(this.selectedSupplier.id, payload)
      : this.mystoreService.createSupplier(payload);
    request.subscribe({
      next: () => {
        this.showSupplierModal = false;
        this.feedback = 'Fornecedor salvo com sucesso.';
        this.loadSuppliers();
        this.loadLookups();
      },
      error: (error) => this.errorMessage = error?.error?.error || 'Falha ao salvar o fornecedor.'
    });
  }

  deleteSupplier(supplier: MyStoreSupplier): void {
    if (!this.tenantId || !this.userId) return;
    this.mystoreService.deleteSupplier(this.tenantId, this.userId, supplier.id).subscribe({
      next: () => {
        this.feedback = 'Fornecedor removido com sucesso.';
        this.loadSuppliers();
        this.loadLookups();
      },
      error: (error) => this.errorMessage = error?.error?.error || 'Falha ao excluir o fornecedor.'
    });
  }

  openBudgetModal(budget?: MyStoreBudget): void {
    this.selectedBudget = budget || null;
    while (this.budgetItems.length) this.budgetItems.removeAt(0);
    if (budget?.id && this.tenantId) {
      this.mystoreService.getBudget(this.tenantId, budget.id).subscribe({
        next: (detail) => {
          this.budgetForm.reset({
            store_id: detail.store_id || '',
            client_id: detail.client_id || '',
            seller_user_id: detail.seller_user_id || '',
            status: detail.status || 'DRAFT',
            valid_until: detail.valid_until || '',
            notes: detail.notes || ''
          });
          (detail.items || []).forEach((item) => this.addBudgetItem(item));
          if (!detail.items?.length) this.addBudgetItem();
          this.showBudgetModal = true;
        }
      });
      return;
    }
    this.budgetForm.reset({
      store_id: '',
      client_id: '',
      seller_user_id: '',
      status: 'DRAFT',
      valid_until: '',
      notes: ''
    });
    this.addBudgetItem();
    this.showBudgetModal = true;
  }

  submitBudget(): void {
    if (this.budgetForm.invalid) return;
    const payload = this.withAudit({ ...this.budgetForm.getRawValue(), items: this.budgetItems.getRawValue() });
    const request = this.selectedBudget
      ? this.mystoreService.updateBudget(this.selectedBudget.id, payload)
      : this.mystoreService.createBudget(payload);
    request.subscribe({
      next: () => {
        this.showBudgetModal = false;
        this.feedback = 'Orçamento salvo com sucesso.';
        this.loadBudgets();
        this.loadDashboard();
      },
      error: (error) => this.errorMessage = error?.error?.error || 'Falha ao salvar o orçamento.'
    });
  }

  convertBudget(budget: MyStoreBudget): void {
    if (!this.userId) return;
    this.mystoreService.convertBudgetToSale(budget.id, this.withAudit({})).subscribe({
      next: () => {
        this.feedback = 'Orçamento convertido em venda.';
        this.loadBudgets();
        this.loadSales();
        this.loadStock();
        this.loadDashboard();
      },
      error: (error) => this.errorMessage = error?.error?.error || 'Falha ao converter o orçamento.'
    });
  }

  deleteBudget(budget: MyStoreBudget): void {
    if (!this.tenantId || !this.userId) return;
    this.mystoreService.deleteBudget(this.tenantId, this.userId, budget.id).subscribe({
      next: () => {
        this.feedback = 'Orçamento removido com sucesso.';
        this.loadBudgets();
      },
      error: (error) => this.errorMessage = error?.error?.error || 'Falha ao excluir o orçamento.'
    });
  }

  openSaleModal(sale?: MyStoreSale): void {
    this.selectedSale = sale || null;
    while (this.saleItems.length) this.saleItems.removeAt(0);
    while (this.salePayments.length) this.salePayments.removeAt(0);
    if (sale?.id && this.tenantId) {
      this.mystoreService.getSale(this.tenantId, sale.id).subscribe({
        next: (detail) => {
          this.saleForm.reset({
            store_id: detail.store_id || '',
            client_id: detail.client_id || '',
            seller_user_id: detail.seller_user_id || '',
            source_budget_id: detail.source_budget_id || '',
            status: detail.status || 'OPEN',
            sold_at: this.asDateTimeLocal(detail.sold_at),
            notes: detail.notes || ''
          });
          (detail.items || []).forEach((item) => this.addSaleItem(item));
          (detail.payments || []).forEach((payment) => this.addSalePaymentLine(payment));
          if (!detail.items?.length) this.addSaleItem();
          this.showSaleModal = true;
        }
      });
      return;
    }
    this.saleForm.reset({
      store_id: '',
      client_id: '',
      seller_user_id: '',
      source_budget_id: '',
      status: 'OPEN',
      sold_at: '',
      notes: ''
    });
    this.addSaleItem();
    this.addSalePaymentLine();
    this.showSaleModal = true;
  }

  submitSale(): void {
    if (this.saleForm.invalid) return;
    const payload = this.withAudit({
      ...this.saleForm.getRawValue(),
      items: this.saleItems.getRawValue(),
      payments: this.salePayments.getRawValue()
    });
    const request = this.selectedSale
      ? this.mystoreService.updateSale(this.selectedSale.id, payload)
      : this.mystoreService.createSale(payload);
    request.subscribe({
      next: () => {
        this.showSaleModal = false;
        this.feedback = 'Venda salva com sucesso.';
        this.loadSales();
        this.loadDashboard();
        this.loadStock();
        this.loadCashSessions();
      },
      error: (error) => this.errorMessage = error?.error?.error || 'Falha ao salvar a venda.'
    });
  }

  openPaymentModal(sale: MyStoreSale): void {
    this.selectedSale = sale;
    this.paymentForm.reset({
      payment_method: 'PIX',
      amount: 0,
      paid_at: '',
      notes: ''
    });
    this.showPaymentModal = true;
  }

  submitPayment(): void {
    if (!this.selectedSale || this.paymentForm.invalid) return;
    const payload = this.withAudit(this.paymentForm.getRawValue());
    this.mystoreService.addSalePayment(this.selectedSale.id, payload).subscribe({
      next: () => {
        this.showPaymentModal = false;
        this.feedback = 'Pagamento registrado com sucesso.';
        this.loadSales();
        this.loadCashSessions();
      },
      error: (error) => this.errorMessage = error?.error?.error || 'Falha ao registrar o pagamento.'
    });
  }

  deleteSale(sale: MyStoreSale): void {
    if (!this.tenantId || !this.userId) return;
    this.mystoreService.deleteSale(this.tenantId, this.userId, sale.id).subscribe({
      next: () => {
        this.feedback = 'Venda removida com sucesso.';
        this.loadSales();
      },
      error: (error) => this.errorMessage = error?.error?.error || 'Falha ao excluir a venda.'
    });
  }

  openPurchaseModal(purchase?: MyStorePurchase): void {
    this.selectedPurchase = purchase || null;
    while (this.purchaseItems.length) this.purchaseItems.removeAt(0);
    if (purchase?.id && this.tenantId) {
      this.mystoreService.getPurchase(this.tenantId, purchase.id).subscribe({
        next: (detail) => {
          this.purchaseForm.reset({
            store_id: detail.store_id || '',
            supplier_id: detail.supplier_id || '',
            buyer_user_id: detail.buyer_user_id || '',
            status: detail.status || 'OPEN',
            purchased_at: this.asDateTimeLocal(detail.purchased_at),
            notes: detail.notes || ''
          });
          (detail.items || []).forEach((item) => this.addPurchaseItem(item));
          if (!detail.items?.length) this.addPurchaseItem();
          this.showPurchaseModal = true;
        }
      });
      return;
    }
    this.purchaseForm.reset({
      store_id: '',
      supplier_id: '',
      buyer_user_id: '',
      status: 'OPEN',
      purchased_at: '',
      notes: ''
    });
    this.addPurchaseItem();
    this.showPurchaseModal = true;
  }

  submitPurchase(): void {
    if (this.purchaseForm.invalid) return;
    const payload = this.withAudit({
      ...this.purchaseForm.getRawValue(),
      items: this.purchaseItems.getRawValue()
    });
    const request = this.selectedPurchase
      ? this.mystoreService.updatePurchase(this.selectedPurchase.id, payload)
      : this.mystoreService.createPurchase(payload);
    request.subscribe({
      next: () => {
        this.showPurchaseModal = false;
        this.feedback = 'Compra salva com sucesso.';
        this.loadPurchases();
        this.loadStock();
        this.loadDashboard();
      },
      error: (error) => this.errorMessage = error?.error?.error || 'Falha ao salvar a compra.'
    });
  }

  deletePurchase(purchase: MyStorePurchase): void {
    if (!this.tenantId || !this.userId) return;
    this.mystoreService.deletePurchase(this.tenantId, this.userId, purchase.id).subscribe({
      next: () => {
        this.feedback = 'Compra removida com sucesso.';
        this.loadPurchases();
      },
      error: (error) => this.errorMessage = error?.error?.error || 'Falha ao excluir a compra.'
    });
  }

  openStockMovementModal(): void {
    this.stockMovementForm.reset({
      store_id: this.filters.stock.store_id || '',
      item_id: '',
      movement_type: 'ENTRY',
      quantity: 1,
      unit_cost: 0,
      notes: ''
    });
    this.showStockMovementModal = true;
  }

  submitStockMovement(): void {
    if (this.stockMovementForm.invalid) return;
    const payload = this.withAudit(this.stockMovementForm.getRawValue());
    this.mystoreService.createStockMovement(payload).subscribe({
      next: () => {
        this.showStockMovementModal = false;
        this.feedback = 'Movimentação de estoque registrada.';
        this.loadStock();
        this.loadDashboard();
      },
      error: (error) => this.errorMessage = error?.error?.error || 'Falha ao registrar a movimentação.'
    });
  }

  openCashSessionModal(): void {
    this.cashSessionForm.reset({
      store_id: '',
      opening_amount: 0,
      notes: ''
    });
    this.showCashSessionModal = true;
  }

  submitCashSession(): void {
    if (this.cashSessionForm.invalid) return;
    const payload = this.withAudit(this.cashSessionForm.getRawValue());
    this.mystoreService.openCashSession(payload).subscribe({
      next: () => {
        this.showCashSessionModal = false;
        this.feedback = 'Caixa aberto com sucesso.';
        this.loadCashSessions();
      },
      error: (error) => this.errorMessage = error?.error?.error || 'Falha ao abrir o caixa.'
    });
  }

  closeCashSession(session: MyStoreCashSession): void {
    if (!this.userId) return;
    const closingAmount = Number(prompt('Informe o valor de fechamento do caixa:', String(session.expected_amount ?? 0)) || 0);
    this.mystoreService.closeCashSession(session.id, this.withAudit({ closing_amount: closingAmount })).subscribe({
      next: () => {
        this.feedback = 'Caixa fechado com sucesso.';
        this.loadCashSessions();
      },
      error: (error) => this.errorMessage = error?.error?.error || 'Falha ao fechar o caixa.'
    });
  }

  openCashMovements(session: MyStoreCashSession): void {
    if (!this.tenantId) return;
    this.selectedCashSession = session;
    this.cashMovementForm.reset({
      movement_type: 'IN',
      category: '',
      amount: 0,
      moved_at: '',
      notes: ''
    });
    this.mystoreService.getCashMovements(this.tenantId, session.id).subscribe({
      next: (rows) => {
        this.cashMovements = rows || [];
        this.showCashMovementModal = true;
      },
      error: (error) => this.errorMessage = error?.error?.error || 'Falha ao carregar as movimentações do caixa.'
    });
  }

  submitCashMovement(): void {
    if (!this.selectedCashSession || this.cashMovementForm.invalid) return;
    const payload = this.withAudit(this.cashMovementForm.getRawValue());
    this.mystoreService.createCashMovement(this.selectedCashSession.id, payload).subscribe({
      next: () => {
        this.feedback = 'Movimentação de caixa registrada.';
        this.openCashMovements(this.selectedCashSession as MyStoreCashSession);
        this.loadCashSessions();
      },
      error: (error) => this.errorMessage = error?.error?.error || 'Falha ao registrar a movimentação do caixa.'
    });
  }

  asCurrency(value: unknown): string {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value || 0));
  }

  asDateTimeLocal(value: unknown): string {
    if (!value) return '';
    const date = new Date(String(value));
    if (Number.isNaN(date.getTime())) return '';
    const pad = (num: number) => String(num).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  hasNext(total: number, filter: { page: number; page_size: number }): boolean {
    return filter.page * filter.page_size < total;
  }

  prevPage(filter: { page: number }, reload: () => void): void {
    if (filter.page <= 1) return;
    filter.page -= 1;
    reload();
  }

  nextPage(total: number, filter: { page: number; page_size: number }, reload: () => void): void {
    if (!this.hasNext(total, filter)) return;
    filter.page += 1;
    reload();
  }

  closeModal(modal: string): void {
    switch (modal) {
      case 'store': this.showStoreModal = false; break;
      case 'storeUsers': this.showStoreUsersModal = false; break;
      case 'client': this.showClientModal = false; break;
      case 'category': this.showCategoryModal = false; break;
      case 'item': this.showItemModal = false; break;
      case 'supplier': this.showSupplierModal = false; break;
      case 'budget': this.showBudgetModal = false; break;
      case 'sale': this.showSaleModal = false; break;
      case 'purchase': this.showPurchaseModal = false; break;
      case 'stock': this.showStockMovementModal = false; break;
      case 'cash': this.showCashSessionModal = false; break;
      case 'cashMovement': this.showCashMovementModal = false; break;
      case 'payment': this.showPaymentModal = false; break;
      default: break;
    }
  }
}
